import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "./ui/index.ts";
import { I } from "./icons/icons.tsx";
import { searchIncludes } from "./text-search.ts";

export interface ActOption {
  id: string;
  label: string;
  color: string;
  duration: number;
}

const inputClass =
  "h-9 w-full rounded-[var(--sf-radius)] border border-[var(--sf-border-strong)] bg-[var(--sf-surface)] pr-8 pl-7 text-[13.5px] font-[inherit] text-[var(--sf-text)] outline-none focus:border-[var(--sf-primary)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--sf-primary)_12%,transparent)]";

export function ActSelect({
  acts,
  value,
  onChange,
  placeholder = "Rechercher un acte…",
  className,
}: {
  acts: ActOption[];
  value: string;
  onChange: (act: ActOption) => void;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState<string | null>(null);
  const [highlight, setHighlight] = React.useState(0);
  const [rect, setRect] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const listId = React.useId();

  const selected = acts.find((a) => a.id === value) ?? null;
  const searching = query !== null;
  const displayValue = searching ? query : (selected?.label ?? "");

  const filtered = React.useMemo(() => {
    const q = (query ?? "").trim();
    if (!q) return acts;
    return acts.filter((a) => searchIncludes(a.label, q));
  }, [acts, query]);

  React.useEffect(() => {
    if (!open) return;
    if (!searching) {
      const idx = filtered.findIndex((a) => a.id === value);
      setHighlight(idx >= 0 ? idx : 0);
    } else {
      setHighlight(0);
    }
  }, [open, searching, filtered, value]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      setOpen(false);
      setQuery(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-option-index="${highlight}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [open, highlight]);

  const pick = (act: ActOption) => {
    onChange(act);
    setOpen(false);
    setQuery(null);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && open && filtered[highlight]) {
      e.preventDefault();
      pick(filtered[highlight]!);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery(null);
      inputRef.current?.blur();
    }
  };

  const dotColor = selected?.color ?? "transparent";

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <div ref={triggerRef} className="relative">
        <span
          className="pointer-events-none absolute top-1/2 left-2.5 size-2 -translate-y-1/2 rounded-full"
          style={{ background: dotColor }}
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onClick={(e) => {
            setOpen(true);
            setQuery("");
            e.currentTarget.select();
          }}
          onBlur={() => {
            setOpen(false);
            setQuery(null);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className={inputClass}
          autoComplete="off"
        />
        <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-sf-text-muted">
          <I.ChevronDown
            size={14}
            className={cn(
              "transition-transform duration-150",
              open && "rotate-180",
            )}
          />
        </span>
      </div>

      {open &&
        rect &&
        filtered.length > 0 &&
        createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              zIndex: 600,
            }}
            className="max-h-[min(320px,48vh)] overflow-y-auto rounded-[var(--sf-radius-lg)] border border-sf-border bg-white py-1 shadow-[var(--sf-shadow-lg)]"
          >
            {filtered.map((act, i) => (
              <li key={act.id} role="option" aria-selected={act.id === value}>
                <button
                  type="button"
                  tabIndex={-1}
                  data-option-index={i}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(act)}
                  onMouseEnter={() => setHighlight(i)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-2.5 border-none px-2.5 py-2 text-left font-[inherit] transition-colors",
                    i === highlight
                      ? "bg-sf-primary-50"
                      : "bg-transparent hover:bg-gray-50",
                  )}
                >
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ background: act.color }}
                  />
                  <span className="flex-1 truncate text-[13px] font-medium text-sf-text">
                    {act.label}
                  </span>
                  <span className="text-[11.5px] text-sf-text-muted">
                    {act.duration} min
                  </span>
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}

      {open &&
        rect &&
        filtered.length === 0 &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              zIndex: 600,
            }}
            className="rounded-[var(--sf-radius-lg)] border border-sf-border bg-white px-3 py-2.5 text-[12.5px] text-sf-text-muted shadow-[var(--sf-shadow-lg)]"
          >
            Aucun acte trouvé
          </div>,
          document.body,
        )}
    </div>
  );
}
